import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, PlayCircle, FileWarning, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  adminDisputeDetail,
  adminCloseDispute,
  adminRecordingSignedUrl,
  adminRefundOverride,
  adminForceClose,
} from '../../api/adminO2O';

const MIN_REASON = 20;

const eventBadgeColor = (event) => {
  if (event.startsWith('ADMIN_')) return 'bg-purple-500/15 text-purple-200 border-purple-500/30';
  if (event.includes('DISPUTE')) return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (event.includes('REFUND') || event.includes('CANCEL')) return 'bg-red-500/15 text-red-200 border-red-500/30';
  if (event.includes('RECORDING')) return 'bg-blue-500/15 text-blue-200 border-blue-500/30';
  if (event.includes('COMPLETED') || event.includes('STARTED')) return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30';
  return 'bg-neutral-500/15 text-neutral-200 border-neutral-500/30';
};

const ReasonModal = ({ open, onClose, title, description, submitLabel, onSubmit, extraFields = null }) => {
  const { t } = useTranslation('admin');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setReason(''); }, [open]);
  const valid = reason.trim().length >= MIN_REASON;
  const handle = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onSubmit(reason.trim());
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid="reason-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {extraFields}
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={5}
          placeholder={t('detail_extras.min_chars_hint', { n: MIN_REASON })}
          data-testid="reason-textarea"
        />
        <p className="text-right text-[10px] text-muted-foreground">
          {reason.length} / 2000 · min {MIN_REASON}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="reason-cancel">{t('detail_extras.cancel')}</Button>
          <Button onClick={handle} disabled={!valid || busy} className="gold-gradient" data-testid="reason-submit">
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RecordingViewerModal = ({ open, onClose, disputeId }) => {
  const { t } = useTranslation('admin');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const [audioOnly, setAudioOnly] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) {
      setReason(''); setUrl(null); setError(null);
    }
  }, [open]);
  const valid = reason.trim().length >= MIN_REASON;
  const handleFetch = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      const res = await adminRecordingSignedUrl(disputeId, { reason: reason.trim() });
      setUrl(res.signed_url);
      setAudioOnly(!!res.audio_only);
      toast.success(t('detail_extras.success_signed_url', { minutes: Math.floor((res.expires_in || 0) / 60) }));
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || t('detail_extras.generic_error');
      setError(String(d));
    } finally {
      setBusy(false);
    }
  };
  const isMock = url && url.startsWith('MOCK://');
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="recording-viewer-modal">
        <DialogHeader>
          <DialogTitle>{t('recording_modal.title')}</DialogTitle>
          <DialogDescription>
            {t('recording_modal.subtitle')}
          </DialogDescription>
        </DialogHeader>
        {!url && (
          <>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder={t('detail_extras.min_chars_hint', { n: MIN_REASON })}
              data-testid="recording-viewer-reason"
            />
            <p className="text-right text-[10px] text-muted-foreground">
              {reason.length} · min {MIN_REASON}
            </p>
            {error && (
              <p className="rounded-md border border-red-500/20 bg-red-500/5 p-2 text-xs text-red-200">{error}</p>
            )}
          </>
        )}
        {url && (
          <div className="space-y-3">
            {isMock ? (
              <div className="rounded-md border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200">
                {t('detail_extras.preview_notice', { key: url })}
              </div>
            ) : audioOnly ? (
              <audio controls src={url} className="w-full" data-testid="recording-viewer-audio" />
            ) : (
              <video controls src={url} className="w-full rounded-md" data-testid="recording-viewer-video" />
            )}
            <p className="break-all text-[10px] text-muted-foreground">{url}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="recording-viewer-close">{t('recording_modal.close')}</Button>
          {!url && (
            <Button onClick={handleFetch} disabled={!valid || busy} className="gold-gradient" data-testid="recording-viewer-fetch">
              {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              {t('recording_modal.emit_cta')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CloseDisputeModal = ({ open, onClose, disputeId, onDone }) => {
  const { t } = useTranslation('admin');
  const [resolution, setResolution] = useState('REJECTED');
  const [issueRefund, setIssueRefund] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setResolution('REJECTED'); setIssueRefund(false); setNotes(''); }
  }, [open]);
  const valid = notes.trim().length >= MIN_REASON;
  const handle = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      const res = await adminCloseDispute(disputeId, {
        resolution, adminNotes: notes.trim(), issueRefund,
      });
      toast.success(t('detail_extras.success_close_dispute', { resolution, extra: res?.refund ? ' + refund' : '' }));
      onDone && onDone();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message;
      toast.error(String(d));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid="close-dispute-modal">
        <DialogHeader>
          <DialogTitle>{t('close_modal.title')}</DialogTitle>
          <DialogDescription>
            {t('close_modal.subtitle')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              variant={resolution === 'REJECTED' ? 'default' : 'outline'}
              onClick={() => setResolution('REJECTED')}
              size="sm"
              data-testid="close-dispute-rejected-btn"
            >
              {t('close_modal.rejected')}
            </Button>
            <Button
              variant={resolution === 'ACCEPTED' ? 'default' : 'outline'}
              onClick={() => setResolution('ACCEPTED')}
              size="sm"
              data-testid="close-dispute-accepted-btn"
            >
              {t('close_modal.accepted')}
            </Button>
          </div>
          {resolution === 'ACCEPTED' && (
            <label className="flex items-center gap-2 rounded-md border border-border p-2 text-xs">
              <input
                type="checkbox"
                checked={issueRefund}
                onChange={(e) => setIssueRefund(e.target.checked)}
                data-testid="close-dispute-refund-checkbox"
              />
              {t('close_modal.issue_refund')}
            </label>
          )}
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            placeholder={t('detail_extras.notes_placeholder', { n: MIN_REASON })}
            data-testid="close-dispute-notes"
          />
          <p className="text-right text-[10px] text-muted-foreground">
            {notes.length} · min {MIN_REASON}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('detail_extras.cancel')}</Button>
          <Button onClick={handle} disabled={!valid || busy} className="gold-gradient" data-testid="close-dispute-submit">
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            {t('close_modal.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DisputeDetailPanel = ({ disputeId, onClose, onChanged }) => {
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(locale) : '—');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(null); // 'close' | 'recording' | 'refund' | 'force'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminDisputeDetail(disputeId);
      setDetail(d); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setLoading(false);
    }
  }, [disputeId]);
  useEffect(() => { if (disputeId) load(); }, [disputeId, load]);

  if (loading) return (
    <div className="flex h-96 flex-col items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin gold-text" />
      <p className="text-sm">{t('detail_extras.detail_loading')}</p>
    </div>
  );
  if (error || !detail) return (
    <div className="p-6 text-center text-red-200">
      <FileWarning className="mx-auto mb-2 h-6 w-6" />
      {error || t('detail_extras.detail_unavailable')}
    </div>
  );

  const { dispute, session, recording, purchases, timeline, creator, buyer } = detail;
  const canClose = ['OPEN', 'REVIEWING'].includes(dispute.state);
  const oid = session?.id;

  const handleRefundOverride = async (reason) => {
    try {
      await adminRefundOverride(oid, { reason });
      toast.success(t('detail_extras.success_refund_override'));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('detail_extras.generic_error'));
    }
  };
  const handleForceClose = async (reason) => {
    try {
      await adminForceClose(oid, { reason, issueRefund: true });
      toast.success(t('detail_extras.success_force_close'));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('detail_extras.generic_error'));
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="dispute-detail-panel" data-dispute-id={dispute.id}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.eyebrow')}</p>
          <h2 className="font-mono text-sm">{dispute.id}</h2>
          <p className="text-xs text-muted-foreground">{t('detail.session')}: <span className="font-mono">{dispute.session_id}</span></p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${eventBadgeColor(`DISPUTE_${dispute.state}`)}`}>
            {dispute.state}
          </span>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Actors */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md border border-border p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.creator')}</p>
          <p className="text-sm font-medium">{creator?.username || creator?.id}</p>
          {creator?.email && <p className="text-[10px] text-muted-foreground">{creator.email}</p>}
        </div>
        <div className="rounded-md border border-border p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.buyer')}</p>
          <p className="text-sm font-medium">{buyer?.username || buyer?.id}</p>
          {buyer?.email && <p className="text-[10px] text-muted-foreground">{buyer.email}</p>}
        </div>
      </div>

      {/* Dispute payload */}
      <div className="rounded-md border border-border p-3">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.motivation')}</p>
        <p className="mt-1 text-sm font-medium">{dispute.motivo}</p>
        <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed">{dispute.descrizione}</p>
        <p className="mt-2 text-[10px] text-muted-foreground">{t('detail.opened_at')}: {fmt(dispute.created_at)}</p>
        {dispute.closed_at && (
          <p className="text-[10px] text-muted-foreground">{t('detail.closed_at')}: {fmt(dispute.closed_at)} — {dispute.resolution}</p>
        )}
        {dispute.admin_notes && (
          <p className="mt-2 rounded-md bg-neutral-500/5 p-2 text-xs italic text-muted-foreground">{dispute.admin_notes}</p>
        )}
      </div>

      {/* Session context */}
      {session && (
        <div className="rounded-md border border-border p-3 text-xs">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.session')}</p>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <p>{t('detail.session_meta.state')}: <b>{session.state}</b></p>
            <p>{t('detail.session_meta.payment_status')}: <b>{session.payment_status}</b></p>
            <p>{t('detail.session_meta.price')}: {(session.price_cents / 100).toFixed(2)} €</p>
            <p>{t('detail.session_meta.duration')}: {session.duration_minutes} min</p>
            <p>{t('detail.session_meta.start')}: {fmt(session.slot_start_utc)}</p>
            <p>{t('detail.session_meta.end')}: {fmt(session.session_ended_at || session.slot_end_utc)}</p>
          </div>
        </div>
      )}

      {/* Recording metadata */}
      {recording && (
        <div className="rounded-md border border-border p-3 text-xs">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.recording.title')}</p>
          <p>{t('detail.recording.status')}: <b>{recording.status}</b>{recording.dispute_hold && ` · ${t('detail.recording.hold')}`}</p>
          <p>{t('detail.recording.object_key')}: <code className="break-all">{recording.object_key}</code></p>
          {recording.recording_duration && <p>{t('detail.recording.duration')}: {recording.recording_duration}{t('detail.recording.seconds')}</p>}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setOpenModal('recording')}
            disabled={recording.status === 'deleted'}
            data-testid="dispute-open-recording-btn"
          >
            <PlayCircle className="mr-1 h-3.5 w-3.5" /> {t('detail.recording.signed_url_cta')}
          </Button>
        </div>
      )}

      {/* Purchases */}
      {purchases && purchases.length > 0 && (
        <div className="rounded-md border border-border p-3 text-xs">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.payout.title')}</p>
          {purchases.map((p) => (
            <div key={p.id} className="mt-1">
              {t('detail.payout.net')} {p.net_amount} {t('detail.payout.credits')} ({p.net_amount_eur} €) · {p.reversed ? t('detail.payout.reversed') : p.released ? t('detail.payout.released') : p.dispute_frozen ? t('detail.payout.frozen') : t('detail.payout.pending')} · {t('detail.payout.release_at')} {fmt(p.pending_release_at)}
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-md border border-border">
        <div className="border-b border-border p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('detail.timeline_title')}</p>
        </div>
        <ul className="max-h-60 overflow-auto p-2 text-[11px]">
          {timeline.map((e) => (
            <li key={e.id} className="mb-1 flex items-start gap-2">
              <span className={`inline-flex shrink-0 rounded-full border px-1.5 py-0.5 uppercase tracking-wider ${eventBadgeColor(e.event_type)}`}>
                {e.event_type}
              </span>
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground">{fmt(e.created_at)} · {e.actor_type}{e.actor_id ? ` (${e.actor_id.slice(0, 8)})` : ''}</p>
                {e.metadata && Object.keys(e.metadata).length > 0 && (
                  <p className="truncate text-[10px] text-muted-foreground">{JSON.stringify(e.metadata).slice(0, 200)}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Admin actions */}
      <div className="flex flex-wrap gap-2">
        {canClose && (
          <Button
            className="gold-gradient"
            size="sm"
            onClick={() => setOpenModal('close')}
            data-testid="dispute-close-cta"
          >
            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> {t('detail.actions.close')}
          </Button>
        )}
        {session && session.state !== 'REFUNDED' && !session.stripe_refund_id && session.payment_status === 'paid' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenModal('refund')}
            data-testid="dispute-refund-override-cta"
          >
            {t('detail.actions.refund_override')}
          </Button>
        )}
        {session && !['COMPLETED', 'REFUNDED', 'CANCELLED'].includes(session.state) && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setOpenModal('force')}
            data-testid="dispute-force-close-cta"
          >
            {t('detail.actions.force_close')}
          </Button>
        )}
      </div>

      <CloseDisputeModal
        open={openModal === 'close'}
        onClose={() => setOpenModal(null)}
        disputeId={dispute.id}
        onDone={async () => { await load(); onChanged && onChanged(); }}
      />
      <RecordingViewerModal
        open={openModal === 'recording'}
        onClose={() => setOpenModal(null)}
        disputeId={dispute.id}
      />
      <ReasonModal
        open={openModal === 'refund'}
        onClose={() => setOpenModal(null)}
        title={t('refund_modal.title')}
        description={t('refund_modal.subtitle')}
        submitLabel={t('refund_modal.submit')}
        onSubmit={handleRefundOverride}
      />
      <ReasonModal
        open={openModal === 'force'}
        onClose={() => setOpenModal(null)}
        title={t('force_modal.title')}
        description={t('force_modal.subtitle')}
        submitLabel={t('force_modal.submit')}
        onSubmit={handleForceClose}
      />
    </div>
  );
};

export default DisputeDetailPanel;
